import {inject, Injectable, NgZone, PLATFORM_ID} from '@angular/core';
import {bufferTime, Observable, of, Subject} from 'rxjs';
import {Image} from '../../data/models/image';
import {catchError, filter, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {isPlatformBrowser, isPlatformServer} from '@angular/common';

type ImageTypes = 'offer' | 'confirmation' | 'external' | 'article' | 'payment';
type LogEntry = { type: string, id: string|number };

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  private http = inject(HttpClient);
  private platformId = inject<Object>(PLATFORM_ID);
  private ngZone = inject(NgZone, { optional: true });
  private errorLog$ = new Subject<LogEntry>();

  private readonly imageCache: Record<string, Image>;

  constructor() {
    this.imageCache = {};
    this.initErrorLog();
  }

  private initErrorLog() {
    this.ngZone.runOutsideAngular(() => {
      this.errorLog$.pipe(
        bufferTime(2e3), // 2 Sekunden sammeln
        filter(msg => msg.length > 0), // nur loggen, wenn überhaupt was da ist
      ).subscribe(errorMessages => {
        setTimeout(() => {
          let collector = {};
          errorMessages.forEach(msg => {
            collector[msg.type] = collector[msg.type] || [];
            collector[msg.type].push(msg.id);
          });
          let count: number, plural: string;
          for (let type in collector) {
            count = errorMessages.length;
            plural = count == 1 ? '' : 's';
            console.log(`could not load ${count} ${type}-image${plural} for ID${plural}:`, collector[type].join(','));
          }
        }, 0);
      });
    });
  }

  getImageOfOffer(offerId: number): Observable<Image> {
    return this.getImage('offer', offerId, '/webabo/offers/' + offerId + '/images');
  }

  getConfirmationImageOfOffer(offerId: number): Observable<Image> {
    return this.getImage('confirmation', offerId, '/webabo/offers/' + offerId + '/confirmationImages');
  }

  getImageOfExternalOffer(offerId: string) {
    return this.getImage('external', offerId, '/webabo/externalOffer/' + offerId + '/image');
  }

  getImageOfArticle(articleId: string): Observable<Image> {
    return this.getImage('offer', articleId, '/webabo/articles/' + articleId + '/images');
  }

  getImagePaymentType(paymentId: string): Observable<Image> {
    return this.getImage('payment', paymentId, '/webabo/basedata/formOfPayment/' + paymentId + '/image', 400);
  }

  private getImage(type: ImageTypes, id: number | string, url: string, width?: number) {
    if (isPlatformServer(this.platformId)) {
      return of(null);
    }

    width = width ?? this.width;
    if (!url.match(/[?&]width=\d+/)) {
      url = url + '?width=' + width;
    }

    const cacheId = type + ':' + id + ':' + width;
    if (this.imageCache && this.imageCache[cacheId]) {
      return of(this.imageCache[cacheId]);
    }

    return this.http.get<any>(url).pipe(
      map(img => {
        if (img) {
          const image = new Image(img.type, img.src);
          this.imageCache[cacheId] = image;
          return image;
        } else {
          return null;
        }
      }),
      catchError((() => {
        this.errorLog$.next({type: type, id: id});
        return of(null);
      }))
    );
  }

  get width() {
    return this.getOptimalImageWidth();
  }

  get height() {
    return this.getOptimalImageWidth() / 3;
  }

  private getOptimalImageWidth() {
    // Die Annahme ist, dass das Endgerät eine feste Breite hat, deshalb wird nicht auf Größenänderungen reagiert.
    const windowWidth = isPlatformBrowser(this.platformId) ? window.innerWidth : 0;

    if (windowWidth < 768) {
      return 800;
    } else if (windowWidth < 992) {
      return 1200;
    } else if (windowWidth < 1200) {
      return 1200;
    } else {
      return 1900;
    }
  }
}
